<template>
  <div class="app-layout">
    <Sidebar />
    <div class="lesson-detail-container" v-if="lesson">
      <div class="lesson-detail-header">
        <div class="header-left">
          <button @click="$router.push('/lessons')" class="back-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="back-icon">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Lessons
          </button>
          <h2 class="lesson-title">{{ lesson.title }}</h2>
          <div class="lesson-meta">
            <div class="meta-item">
              <span class="meta-label">Course:</span>
              <span class="meta-value">{{ lesson.course?.title }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Status:</span>
              <span class="status-badge" :class="lesson.lesson_status">
                {{ lesson.lesson_status }}
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Duration:</span>
              <span class="meta-value">{{ formatDuration(lesson.duration) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Order:</span>
              <span class="meta-value">{{ lesson.order }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Last Updated:</span>
              <span class="meta-value">{{ formatDate(lesson.updated_at) }}</span>
            </div>
          </div>
        </div>
        
        <div class="header-actions">
          <button @click="editLesson" class="action-button edit-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit Lesson
          </button>
          <button @click="confirmDeleteLesson" class="action-button delete-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            Delete Lesson
          </button>
        </div>
      </div>

      <div class="lesson-content">
        <div class="content-section">
          <div class="section-header">
            <h3>Lesson Preview</h3>
          </div>
          <div class="video-container">
            <video 
              v-if="!videoError"
              ref="videoPlayer"
              controls 
              class="lesson-video" 
              :poster="lesson.lesson_thumbnail_url"
              @error="handleVideoError"
            ></video>
            <div v-if="videoError" class="video-error">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="error-icon">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p>There was an error loading the video. Please try again.</p>
              <button @click="retryVideo" class="retry-button">Retry</button>
            </div>
          </div>
        </div>

        <div class="content-section">
          <div class="section-header">
            <h3>Description</h3>
          </div>
          <div class="description-content">
            <p>{{ lesson.description }}</p>
          </div>
        </div>

        <div class="content-section">
          <div class="section-header">
            <h3>Course Information</h3>
          </div>
          <div class="course-info" v-if="lesson.course">
            <div class="info-item">
              <span class="info-label">Course Title:</span>
              <span class="info-value">{{ lesson.course.title }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Course Status:</span>
              <span class="info-value">{{ lesson.course.status }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Course Description:</span>
              <div class="course-description" v-html="lesson.course.description"></div>
            </div>
          </div>
        </div>

        <div class="content-section" v-if="lesson.reviews && lesson.reviews.length > 0">
          <div class="section-header">
            <h3>Reviews</h3>
          </div>
          <div class="reviews-list">
            <div v-for="review in lesson.reviews" :key="review.id" class="review-item">
              <div class="review-header">
                <div class="review-rating">
                  <span v-for="i in 5" :key="i" class="star" :class="{ 'filled': i <= review.rating }">★</span>
                </div>
                <span class="review-date">{{ formatDate(review.created_at) }}</span>
              </div>
              <p class="review-comment">{{ review.comment }}</p>
            </div>
          </div>
        </div>

        <div class="content-section" v-else>
          <div class="section-header">
            <h3>Reviews</h3>
          </div>
          <div class="empty-reviews">
            <p>No reviews available for this lesson yet.</p>
          </div>
        </div>

        <div class="content-section">
          <div class="section-header">
            <h3>Technical Details</h3>
          </div>
          <div class="technical-details">
            <div class="detail-item">
              <span class="detail-label">Lesson ID:</span>
              <span class="detail-value">{{ lesson.id }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Video ID:</span>
              <span class="detail-value">{{ lesson.lesson_video_id }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Visibility:</span>
              <span class="detail-value">{{ lesson.is_hidden ? 'Hidden' : 'Visible' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Downloadable:</span>
              <span class="detail-value">{{ lesson.downloadable_url ? 'Yes' : 'No' }}</span>
            </div>
            <div class="detail-item" v-if="lesson.downloadable_url">
              <span class="detail-label">Download URL:</span>
              <a :href="lesson.downloadable_url" target="_blank" class="download-link">Download Video</a>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Dialog -->
      <div v-if="showDeleteDialog" class="dialog-overlay">
        <div class="dialog-content delete-dialog">
          <div class="dialog-header">
            <h2>Delete Lesson</h2>
            <button @click="showDeleteDialog = false" class="close-button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="delete-content">
            <p>Are you sure you want to delete this lesson?</p>
            <p class="warning">This action cannot be undone.</p>
          </div>
          <div class="form-actions">
            <button @click="showDeleteDialog = false" class="cancel-button">Cancel</button>
            <button @click="deleteLesson" class="delete-button" :disabled="isSubmitting">
              {{ isSubmitting ? 'Deleting...' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="loading" class="loading-state">
      <div class="loader"></div>
      <p>Loading lesson details...</p>
    </div>

    <div v-else class="error-state">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="error-icon">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <h3>Lesson Not Found</h3>
      <p>The lesson you're looking for doesn't exist or you don't have permission to view it.</p>
      <button @click="$router.push('/lessons')" class="back-button">
        Back to Lessons
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';

export default {
  name: 'LessonDetail',
  components: {
    Sidebar
  },
  data() {
    return {
      lesson: null,
      loading: true,
      error: null,
      showDeleteDialog: false,
      isSubmitting: false,
      hls: null,
      videoInitialized: false,
      videoError: false
    };
  },
  computed: {
    lessonId() {
      return this.$route.params.id;
    }
  },
  methods: {
    formatDuration(seconds) {
      if (!seconds) return '0:00';
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    },
    async fetchLessonDetails() {
      try {
        this.loading = true;
        const token = localStorage.getItem('token');
        
        const response = await axios.get(`https://api.kewaar.com/api/admin/lessons1/${this.lessonId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        this.lesson = response.data.data;
        
        // Initialize video player after lesson data is loaded
        this.$nextTick(() => {
          this.initializeVideoPlayer();
        });
      } catch (error) {
        console.error('Error fetching lesson details:', error);
        this.error = error.response?.data?.message || 'Failed to load lesson details';
      } finally {
        this.loading = false;
      }
    },
    initializeVideoPlayer() {
      if (!this.lesson || !this.lesson.lesson_url || this.videoInitialized) return;
      
      // Reset error state
      this.videoError = false;
      
      const videoElement = this.$refs.videoPlayer;
      if (!videoElement) return;
      
      // Set the flag to prevent multiple initializations
      this.videoInitialized = true;
      
      // Check if the URL is an HLS stream (ends with .m3u8)
      if (this.lesson.lesson_url.includes('.m3u8')) {
        // Try to load Hls.js dynamically
        import('hls.js').then(({ default: Hls }) => {
          if (Hls.isSupported()) {
            // Destroy existing HLS instance if it exists
            if (this.hls) {
              this.hls.destroy();
            }
            
            this.hls = new Hls({
              maxBufferLength: 30,
              maxMaxBufferLength: 600,
              enableWorker: true
            });
            this.hls.loadSource(this.lesson.lesson_url);
            this.hls.attachMedia(videoElement);
            this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
              // Do nothing, just load the video
            });
            
            // Add error handling
            this.hls.on(Hls.Events.ERROR, (event, data) => {
              console.error('HLS error:', data);
              if (data.fatal) {
                switch(data.type) {
                  case Hls.ErrorTypes.NETWORK_ERROR:
                    // Try to recover network error
                    console.log('Fatal network error encountered, trying to recover');
                    this.hls.startLoad();
                    break;
                  case Hls.ErrorTypes.MEDIA_ERROR:
                    console.log('Fatal media error encountered, trying to recover');
                    this.hls.recoverMediaError();
                    break;
                  default:
                    // Cannot recover
                    console.log('Fatal error, cannot recover');
                    this.hls.destroy();
                    break;
                }
              }
            });
          } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
            // For Safari which has built-in HLS support
            videoElement.src = this.lesson.lesson_url;
          }
        }).catch(error => {
          console.error('Failed to load HLS.js:', error);
          // Fallback to direct source if HLS.js fails to load
          videoElement.src = this.lesson.lesson_url;
        });
      } else {
        // For regular video formats
        videoElement.src = this.lesson.lesson_url;
      }
    },
    editLesson() {
      // Navigate to edit page or show edit dialog
      this.$router.push(`/lessons/${this.lessonId}/edit`);
    },
    confirmDeleteLesson() {
      this.showDeleteDialog = true;
    },
    async deleteLesson() {
      try {
        this.isSubmitting = true;
        const token = localStorage.getItem('token');
        
        await axios.delete(`https://api.kewaar.com/api/admin/lessons1/${this.lessonId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        this.showDeleteDialog = false;
        this.$router.push('/lessons');
      } catch (error) {
        console.error('Error deleting lesson:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    handleVideoError(event) {
      console.error('Video error:', event);
      this.videoError = true;
      
      // Get the video element
      const videoElement = this.$refs.videoPlayer;
      if (!videoElement) return;
      
      // Clean up resources
      if (this.hls) {
        this.hls.destroy();
        this.hls = null;
      }
      this.videoInitialized = false;
    },
    retryVideo() {
      this.videoError = false;
      this.initializeVideoPlayer();
    }
  },
  created() {
    this.fetchLessonDetails();
  },
  mounted() {
    // If the lesson data is already loaded, initialize the video player
    if (this.lesson && this.lesson.lesson_url) {
      this.initializeVideoPlayer();
    }
  },
  beforeUnmount() {
    // Clean up HLS instance if it exists
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    this.videoInitialized = false;
  }
};
</script>

<style scoped>
.app-layout {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

.lesson-detail-container {
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
  max-width: 1400px;
}

.lesson-detail-header {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #f1f5f9;
  border: none;
  border-radius: 6px;
  color: #475569;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
}

.back-button:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.lesson-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  letter-spacing: -0.025em;
}

.lesson-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 0.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.meta-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.meta-value {
  font-size: 0.9375rem;
  color: #1e293b;
  font-weight: 600;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.uploaded {
  background: #dcfce7;
  color: #166534;
}

.status-badge.pending {
  background: #fef3c7;
  color: #9a3412;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.action-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-button.edit-button {
  background: #dcfce7;
  color: #166534;
}

.action-button.edit-button:hover {
  background: #bbf7d0;
  transform: translateY(-1px);
}

.action-button.delete-button {
  background: #fee2e2;
  color: #b91c1c;
}

.action-button.delete-button:hover {
  background: #fecaca;
  transform: translateY(-1px);
}

.action-icon {
  width: 18px;
  height: 18px;
}

.lesson-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.content-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.section-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.section-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.video-container {
  padding: 1.5rem;
  display: flex;
  justify-content: center;
}

.lesson-video {
  width: 100%;
  max-width: 800px;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.description-content {
  padding: 1.5rem;
}

.description-content p {
  margin: 0;
  color: #334155;
  font-size: 1rem;
  line-height: 1.6;
}

.course-info {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.info-value {
  font-size: 0.9375rem;
  color: #1e293b;
}

.course-description {
  color: #334155;
  font-size: 0.9375rem;
  line-height: 1.6;
}

.reviews-list {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.review-item {
  padding: 1.25rem;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.review-rating {
  display: flex;
  gap: 0.25rem;
}

.star {
  color: #cbd5e1;
  font-size: 1.25rem;
}

.star.filled {
  color: #f59e0b;
}

.review-date {
  font-size: 0.75rem;
  color: #64748b;
}

.review-comment {
  margin: 0;
  color: #334155;
  font-size: 0.9375rem;
  line-height: 1.6;
}

.technical-details {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.detail-value {
  font-size: 0.9375rem;
  color: #1e293b;
  word-break: break-all;
}

.download-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.download-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.dialog-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.dialog-header h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  letter-spacing: -0.025em;
}

.close-button {
  background: #f1f5f9;
  border: none;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  color: #475569;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.close-button svg {
  width: 20px;
  height: 20px;
}

.delete-content {
  padding: 1.5rem;
  text-align: center;
}

.delete-content p {
  margin: 0;
  color: #475569;
  font-size: 1rem;
}

.delete-content .warning {
  margin-top: 0.75rem;
  color: #dc2626;
  font-weight: 600;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem 1.5rem 1.5rem;
}

.cancel-button,
.delete-button {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-button {
  background: #f1f5f9;
  border: none;
  color: #475569;
}

.cancel-button:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.delete-button {
  background: #dc2626;
  border: none;
  color: white;
}

.delete-button:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-1px);
}

.delete-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  color: #64748b;
  gap: 1.5rem;
}

.loader {
  width: 48px;
  height: 48px;
  border: 5px solid #e2e8f0;
  border-bottom-color: #3b82f6;
  border-radius: 50%;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  padding: 2rem;
  text-align: center;
}

.error-icon {
  width: 64px;
  height: 64px;
  color: #ef4444;
  margin-bottom: 1.5rem;
}

.error-state h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1rem;
}

.error-state p {
  color: #64748b;
  font-size: 1rem;
  max-width: 500px;
  margin: 0 0 2rem;
}

.empty-reviews {
  padding: 2rem;
  text-align: center;
  color: #64748b;
  font-style: italic;
}

.video-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  text-align: center;
  gap: 1rem;
}

.retry-button {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #dcfce7;
  color: #166534;
}

.retry-button:hover {
  background: #bbf7d0;
  transform: translateY(-1px);
}

@media (max-width: 1024px) {
  .lesson-detail-container {
    margin-left: 0;
    padding: 1rem;
  }

  .lesson-detail-header {
    flex-direction: column;
    gap: 1.5rem;
  }

  .header-actions {
    width: 100%;
  }

  .action-button {
    flex: 1;
    justify-content: center;
  }

  .lesson-meta {
    flex-direction: column;
    gap: 0.75rem;
  }
}
</style> 